import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastType } from '../components/Toast';
import ToastComponent from '../components/Toast';

/** Non-error toasts stay short; errors stay a bit longer for multi-line messages (e.g. upload failures). */
const DEFAULT_TOAST_MS = 5000;
const ERROR_TOAST_MS = 10_000;

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((type: ToastType, message: string, duration?: number) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const resolvedDuration =
      duration !== undefined
        ? duration
        : type === 'error'
          ? ERROR_TOAST_MS
          : DEFAULT_TOAST_MS;
    setToasts((prev) => [{ id, type, message, duration: resolvedDuration }, ...prev]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed z-50 flex max-h-[min(70vh,32rem)] w-full max-w-md flex-col gap-2 overflow-y-auto overflow-x-hidden px-4 sm:px-0 top-[max(1rem,env(safe-area-inset-top))] left-0 right-0 mx-auto sm:left-auto sm:right-4 sm:mx-0 items-stretch sm:items-end"
        aria-label="Notifications"
      >
        {toasts.map(toast => (
          <ToastComponent key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

